import { supabase } from '../lib/supabase';
import type { User, UserConsent, UserConsentRequest } from '../types/user';
import { runOnlineOrToast } from '../utils/networkGuard';
// OBS: Tipos do Supabase ainda nÃ£o gerados; usar any temporariamente
type Profile = any;
type ProfileInsert = any;
type ProfileUpdate = any;

class SupabaseUserService {
  private static instance: SupabaseUserService;

  private constructor() {}

  public static getInstance(): SupabaseUserService {
    if (!SupabaseUserService.instance) {
      SupabaseUserService.instance = new SupabaseUserService();
    }
    return SupabaseUserService.instance;
  }

  // Criar perfil de usuÃ¡rio
  async createUserProfile(userId: string, userData: { name: string; email: string; role?: string }): Promise<Profile> {
    const profileData: ProfileInsert = {
      id: userId,
      full_name: userData.name,
      email: userData.email,
      role: userData.role || 'user',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('profiles')
        .insert(profileData)
        .select('id, full_name, email, role, avatar_url, date_of_birth, created_at, updated_at').single()
    );

    if (error) {
      throw new Error(`Erro ao criar perfil: ${error.message}`);
    }

    return data;
  }

  // Obter perfil de usuÃ¡rio via API REST
  async getUserProfile(userId: string): Promise<Profile | null> {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('ConfiguraÃ§Ã£o do Supabase nÃ£o encontrada.');
    }

    // Para fazer a chamada via REST, precisamos de um token de acesso vÃ¡lido.
    // Vamos tentar pegar do localStorage, onde o AuthContext salvou.
    const tokenData = JSON.parse(localStorage.getItem('supabase.auth.token') || '{}');
    let accessToken: string | undefined = tokenData.access_token;

    // Fallback: tentar obter token diretamente do SDK se localStorage estiver vazio
    if (!accessToken) {
      try {
        const { data } = await supabase.auth.getSession();
        accessToken = data.session?.access_token;
      } catch {
        /* ignore */
      }
      if (!accessToken) {
        console.warn('âš ï¸ [UserService] Token de acesso nÃ£o encontrado para chamada REST. A requisiÃ§Ã£o pode falhar se RLS estiver ativo.');
      }
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=id,full_name,email,role,avatar_url,date_of_birth,created_at,updated_at`,
        {
          method: 'GET',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken || SUPABASE_ANON_KEY}`,
            'Accept': 'application/vnd.pgrst.object+json' // Pede um objeto Ãºnico, similar ao .single()
          },
        }
      );

      if (response.status === 404 || response.status === 406) {
        // 404: NÃ£o encontrado. 406: Not Acceptable (PGRST116 - nenhum resultado encontrado para a query single)
        return null;
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Erro ao buscar perfil (REST): ${error.message || 'Erro desconhecido'}`);
      }

      const data = await response.json();
      return data as Profile;

    } catch (error) {
      // NÃ£o logar erro 401 durante refresh de token (esperado durante inicializaÃ§Ã£o)
      const is401 = error instanceof Error && error.message.includes('JWT expired');
      if (!is401) {
        console.error('âŒ [UserService] ExceÃ§Ã£o ao buscar perfil via REST:', error);
      }
      throw error;
    }
  }

  // Buscar usuÃ¡rio por email
  async getUserByEmail(email: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email, role, avatar_url, date_of_birth, created_at, updated_at')
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw new Error(`Erro ao buscar usuÃ¡rio por email: ${error.message}`);
    }

    return data;
  }

  // Atualizar perfil de usuÃ¡rio
  async updateUserProfile(userId: string, updates: ProfileUpdate): Promise<Profile> {
    const updateData: ProfileUpdate = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId)
        .select('id, full_name, email, role, avatar_url, date_of_birth, created_at, updated_at').single()
    );

    if (error) {
      throw new Error(`Erro ao atualizar perfil: ${error.message}`);
    }

    return data;
  }

  // Verificar se Ã© admin
  async isUserAdmin(userId: string): Promise<boolean> {
    const profile = await this.getUserProfile(userId);
    return profile?.role === 'admin';
  }

  // Promover usuÃ¡rio para admin
  async promoteToAdmin(userId: string): Promise<Profile> {
    return this.updateUserProfile(userId, { role: 'admin' });
  }
  // Remover privilÃ©gios de admin
  async demoteFromAdmin(userId: string): Promise<Profile> {
    return this.updateUserProfile(userId, { role: 'user' });
  }

  // MÃ©todos de consentimento (TO-DO: implementar tabelas no Supabase)
  async registerConsent(consentData: UserConsentRequest): Promise<UserConsent> {
    // 1) Verificar se jÃ¡ existe consentimento para user_id+version
    const { data: existing, error: existingError } = await supabase
      .from('user_consents')
      .select('id, user_id, terms_accepted, privacy_policy_accepted, version, accepted_at, ip_address, user_agent')
      .eq('user_id', consentData.userId)
      .eq('version', consentData.version)
      .order('accepted_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existingError && existingError.code !== 'PGRST116') {
      throw new Error(`Erro ao verificar consentimento existente: ${existingError.message}`);
    }

    if (existing) {
      return {
        id: existing.id,
        userId: existing.user_id,
        termsAccepted: existing.terms_accepted,
        privacyPolicyAccepted: existing.privacy_policy_accepted,
        version: existing.version,
        acceptedAt: existing.accepted_at,
        ipAddress: existing.ip_address ?? undefined,
        userAgent: existing.user_agent ?? undefined
      } as UserConsent;
    }

    // 2) Inserir novo consentimento
    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('user_consents')
        .insert({
          user_id: consentData.userId,
          terms_accepted: consentData.termsAccepted,
          privacy_policy_accepted: consentData.privacyPolicyAccepted,
          version: consentData.version,
          ip_address: consentData.ipAddress,
          user_agent: consentData.userAgent
        })
        .select('id, user_id, terms_accepted, privacy_policy_accepted, version, accepted_at, ip_address, user_agent').single()
    );

    if (error) {
      throw new Error(`Erro ao registrar consentimento: ${error.message}`);
    }

    return {
      id: data.id,
      userId: data.user_id,
      termsAccepted: data.terms_accepted,
      privacyPolicyAccepted: data.privacy_policy_accepted,
      version: data.version,
      acceptedAt: data.accepted_at,
      ipAddress: data.ip_address ?? undefined,
      userAgent: data.user_agent ?? undefined
    } as UserConsent;
  }

  async getConsentHistory(userId: string): Promise<UserConsent[]> {
    const { data, error } = await supabase
      .from('user_consents')
      .select('id, user_id, terms_accepted, privacy_policy_accepted, version, accepted_at, ip_address, user_agent')
      .eq('user_id', userId)
      .order('accepted_at', { ascending: false });

    if (error) {
      throw new Error(`Erro ao buscar histÃ³rico de consentimentos: ${error.message}`);
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      userId: row.user_id,
      termsAccepted: row.terms_accepted,
      privacyPolicyAccepted: row.privacy_policy_accepted,
      version: row.version,
      acceptedAt: row.accepted_at,
      ipAddress: row.ip_address ?? undefined,
      userAgent: row.user_agent ?? undefined
    } as UserConsent));
  }

  async updateConsent(_userId: string, _updates: Partial<UserConsent>): Promise<UserConsent> {
    // TO-DO: Implementar atualizaÃ§Ã£o de consentimentos, caso necessÃ¡rio
    throw new Error('MÃ©todo nÃ£o implementado ainda - aguardando migraÃ§Ã£o completa para Supabase');
  }

  async revokeConsent(_userId: string): Promise<boolean> {
    // TO-DO: Implementar revogaÃ§Ã£o de consentimentos
    throw new Error('MÃ©todo nÃ£o implementado ainda - aguardando migraÃ§Ã£o completa para Supabase');
  }

  async getConsentStats(): Promise<{ total: number; accepted: number; revoked: number }> {
    // TO-DO: Implementar estatÃ­sticas de consentimento
    throw new Error('MÃ©todo nÃ£o implementado ainda - aguardando migraÃ§Ã£o completa para Supabase');
  }

  // UtilitÃ¡rios
  generateUniqueId(): string {
    return crypto.randomUUID();
  }

  // Converter perfil do Supabase para tipo User do sistema
  private profileToUser(profile: Profile): User {
    return {
      id: profile.id,
      name: profile.full_name || '',
      email: profile.email,
      birthDate: profile.date_of_birth || '',
      createdAt: profile.created_at || '',
      updatedAt: profile.updated_at || '',
      role: profile.role || 'user'
    };
  }

  // Obter usuÃ¡rio convertido para o tipo User
  async getUser(userId: string): Promise<User | null> {
    const profile = await this.getUserProfile(userId);
    return profile ? this.profileToUser(profile) : null;
  }

  // Listar todos os usuÃ¡rios (apenas para admins)
  async getAllUsers(): Promise<User[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email, role, avatar_url, date_of_birth, created_at, updated_at')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Erro ao buscar usuÃ¡rios: ${error.message}`);
    }

    return data.map(profile => this.profileToUser(profile));
  }

  // Deletar usuÃ¡rio (apenas para admins)
  async deleteUser(userId: string): Promise<boolean> {
    const { error } = await runOnlineOrToast(async () =>
      supabase
        .from('profiles')
        .delete()
        .eq('id', userId)
    );

    if (error) {
      throw new Error(`Erro ao deletar usuÃ¡rio: ${error.message}`);
    }

    return true;
  }
}

export default SupabaseUserService;
